import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline'

type Props = {
  message?: string
  /** Multiple warnings in one banner; each string is shown on its own line. */
  messages?: string[]
  onDismiss?: () => void
  /** Amber text action on the far right (e.g. "Review roll-forward"). */
  actionLabel?: string
  onAction?: () => void
  className?: string
}

/** Amber warning banner for non-blocking review items. */
export default function WarningBanner({
  message,
  messages,
  onDismiss,
  actionLabel,
  onAction,
  className = '',
}: Props) {
  const lines = messages?.length ? messages : message ? [message] : []
  if (lines.length === 0) {
    return null
  }

  const body =
    lines.length === 1 ? (
      <span className="min-w-0 flex-1 text-sm leading-relaxed">{lines[0]}</span>
    ) : (
      <ul className="min-w-0 flex-1 space-y-1 text-sm leading-relaxed">
        {lines.map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ul>
    )

  const showAction = actionLabel != null && onAction != null
  const trailing =
    showAction || onDismiss ? (
      <div className="flex shrink-0 items-center gap-2">
        {showAction ? (
          <button
            type="button"
            onClick={onAction}
            className="text-sm font-medium text-amber-700 transition-colors hover:text-amber-900"
          >
            {actionLabel}
          </button>
        ) : null}
        {onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-md p-1 text-amber-700 transition-colors hover:bg-amber-100 hover:text-amber-900"
            aria-label="Dismiss"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    ) : null

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950 ${className}`}
      role="status"
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden />
        {body}
      </div>
      {trailing}
    </div>
  )
}
